import React, { useEffect, useRef, useState } from "react";
import { StoryChoice, StoryScene } from "../../../story/storyTypes";

type StoryPageProps = {
  scene: StoryScene;
  storyTitle: string;
  feedback?: string;
  isShaking: boolean;
  onChoice: (choice: StoryChoice) => void;
};

const splitParagraphs = (text?: string) =>
  (text || "")
    .split(/\n\n+/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);

const renderStoryText = (text: string) =>
  text.split(/("(?:[^"]+)")/g).map((part, index) => {
    if (part.startsWith('"') && part.endsWith('"') && part.length > 1) {
      return (
        <em className="story-dialogue" key={`${part}-${index}`}>
          {part}
        </em>
      );
    }

    return <React.Fragment key={`${part}-${index}`}>{part}</React.Fragment>;
  });

function SpeakerIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M4.2 9.2h3.1L12 5.6v12.8l-4.7-3.6H4.2z" fill="currentColor" />
      <path
        className="story-read-wave story-read-wave-1"
        d="M15.1 9.1a3.6 3.6 0 0 1 0 5.8"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeWidth="1.8"
      />
      <path
        className="story-read-wave story-read-wave-2"
        d="M17.8 6.8a7 7 0 0 1 0 10.4"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeWidth="1.8"
      />
    </svg>
  );
}

export function StoryPage({
  scene,
  storyTitle,
  feedback,
  isShaking,
  onChoice,
}: StoryPageProps) {
  const layout = scene.layout || "default";
  const hasChoices = Boolean(scene.choices?.length);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const lyricRefs = useRef<Record<string, HTMLParagraphElement | null>>({});
  const lastActiveLyric = useRef<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playingClip, setPlayingClip] = useState<string | null>(null);
  const [songTime, setSongTime] = useState(0);
  const paragraphs = splitParagraphs(scene.text);
  const activeLyricId =
    scene.lyrics
      ?.flatMap((section, sectionIndex) =>
        section.lines.map((line, lineIndex) => ({
          id: `${sectionIndex}-${lineIndex}`,
          start: line.start,
          end: line.end,
        }))
      )
      ?.find(
        (line) =>
          line.start != null &&
          line.end != null &&
          songTime >= line.start &&
          songTime < line.end
      )?.id ?? null;

  useEffect(() => {
    setIsPlaying(false);
    setPlayingClip(null);
    setSongTime(0);
    lastActiveLyric.current = null;
    const audio = audioRef.current;

    return () => {
      audio?.pause();
    };
  }, [scene.id]);

  useEffect(() => {
    if (!isPlaying) {
      return;
    }

    const syncSongTime = () => {
      setSongTime(audioRef.current?.currentTime ?? 0);
    };

    syncSongTime();
    const timer = window.setInterval(syncSongTime, 80);
    return () => window.clearInterval(timer);
  }, [isPlaying]);

  useEffect(() => {
    if (!activeLyricId || lastActiveLyric.current === activeLyricId) {
      return;
    }

    lastActiveLyric.current = activeLyricId;
    lyricRefs.current[activeLyricId]?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
  }, [activeLyricId]);

  const playAudio = () => {
    if (!audioRef.current) {
      return;
    }

    audioRef.current.currentTime = 0;
    audioRef.current.play();
  };

  const toggleAudio = () => {
    if (!audioRef.current) {
      return;
    }

    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
      return;
    }

    audioRef.current.play();
    setIsPlaying(true);
  };

  const toggleClip = (src: string) => {
    if (!audioRef.current) {
      return;
    }

    if (playingClip === src) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setPlayingClip(null);
      return;
    }

    audioRef.current.src = src;
    audioRef.current.currentTime = 0;
    audioRef.current.play();
    setPlayingClip(src);
  };

  const stopPageTurn = (event: React.SyntheticEvent) => {
    event.stopPropagation();
  };

  if (layout === "cover") {
    return (
      <main className="story-page story-page-cover" aria-label={storyTitle}>
        <section className="story-cover" aria-label="Book cover">
          <img className="story-cover-image" src={scene.image} alt={storyTitle} />
        </section>
      </main>
    );
  }

  if (layout === "spread") {
    return (
      <main className="story-page story-page-spread" aria-label={storyTitle}>
        <section className="story-book-text" aria-label="Story text">
          <audio
            ref={audioRef}
            preload="none"
            onEnded={() => setPlayingClip(null)}
          />
          <div className="story-book-text-inner">
            {paragraphs.map((paragraph, index) => {
              const clip = scene.clips?.[index];

              return (
                <p
                  key={paragraph}
                  className={playingClip === clip ? "story-reading" : undefined}
                >
                  {renderStoryText(paragraph)}
                  {clip && (
                    <button
                      className={`story-read-button${playingClip === clip ? " is-playing" : ""}`}
                      type="button"
                      aria-label="播放這段文字"
                      aria-pressed={playingClip === clip}
                      onClick={() => toggleClip(clip)}
                      onTouchStart={stopPageTurn}
                      onTouchEnd={stopPageTurn}
                    >
                      <SpeakerIcon />
                    </button>
                  )}
                </p>
              );
            })}
          </div>
        </section>
        <section className="story-book-image" aria-label="Story picture">
          <img
            className="story-book-photo"
            src={scene.image}
            alt={`${storyTitle} ${scene.id}`}
          />
        </section>
      </main>
    );
  }

  if (layout === "lyrics") {
    return (
      <main className="story-page story-page-lyrics" aria-label={scene.heading || storyTitle}>
        <section className="story-lyrics" aria-label="Song lyrics">
          {scene.heading && <h2 className="story-lyrics-title">{scene.heading}</h2>}
          {scene.lyrics?.map((section, sectionIndex) => (
            <div className="story-lyric-section" key={`${section.heading}-${sectionIndex}`}>
              <h3>{section.heading}</h3>
              {section.lines.map((line, lineIndex) => {
                const lineId = `${sectionIndex}-${lineIndex}`;

                return (
                  <p
                    key={lineId}
                    ref={(element) => {
                      lyricRefs.current[lineId] = element;
                    }}
                    className={`story-lyric-line${activeLyricId === lineId ? " is-active" : ""}`}
                  >
                    {line.text}
                  </p>
                );
              })}
            </div>
          ))}
          {scene.text && <p className="story-lyrics-note">{scene.text}</p>}
        </section>
        <aside className="story-song-panel">
          {scene.image && (
            <div
              className={`story-vinyl${isPlaying ? " is-spinning" : ""}`}
              aria-hidden="true"
            >
              <div className="story-vinyl-disc">
                <div className="story-vinyl-grooves" />
                <img
                  className="story-vinyl-label"
                  src={scene.image}
                  alt=""
                />
                <div className="story-vinyl-spindle" />
              </div>
              <div className="story-vinyl-arm" />
            </div>
          )}
          {scene.audio && (
            <>
              <audio
                ref={audioRef}
                src={scene.audio}
                preload="auto"
                onEnded={() => {
                  setIsPlaying(false);
                  setSongTime(0);
                }}
              />
              <button
                className="story-song-button"
                type="button"
                onClick={toggleAudio}
              >
                {isPlaying ? "Pause" : "Play Song"}
              </button>
            </>
          )}
        </aside>
      </main>
    );
  }

  return (
    <main
      className={`story-page ${hasChoices ? "story-page-with-menu" : ""} ${
        isShaking ? "story-page-shake" : ""
      }`}
      aria-label={storyTitle}
    >
      <section
        className={`story-illustration ${scene.audio ? "story-illustration-audio" : ""}`}
        aria-label="Story picture"
        onClick={scene.audio ? playAudio : undefined}
      >
        {scene.video ? (
          <video
            className="story-media"
            src={scene.video}
            poster={scene.image}
            controls
            playsInline
          />
        ) : (
          <img className="story-media" src={scene.image} alt={`${storyTitle} ${scene.id}`} />
        )}
        {scene.audio && (
          <>
            <audio ref={audioRef} src={scene.audio} preload="auto" />
            <div className="story-audio-hint">按一下播放聲音</div>
          </>
        )}
      </section>

      {hasChoices && (
        <aside className="story-choice-panel" aria-label="Choose an answer">
          <div className="story-choice-panel-card">
            <p className="story-choice-title">Choose one</p>
            <div className="story-choice-list">
              {scene.choices?.map((choice) => (
                <button
                  key={`${scene.id}-${choice.label}`}
                  className="story-choice-button"
                  type="button"
                  onClick={() => onChoice(choice)}
                >
                  {choice.label}
                </button>
              ))}
            </div>
            {feedback && (
              <div className="story-feedback" role="alert">
                {feedback}
              </div>
            )}
          </div>
        </aside>
      )}

      {scene.text && (
        <section className="story-panel">
          <div className="story-panel-content">
            <p className="story-narration">{scene.text}</p>
          </div>
        </section>
      )}
    </main>
  );
}
